import { useEffect, useRef, useState } from 'react';
import { Container, Button, Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { useCreateCoInvestment, useUploadInvestmentMainImage } from '@/lib/api/investments';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider';
import { CoInvestmentWizard } from '@/features/investments/co-investment/wizard/CoInvestmentWizard';
import { canCreateCoInvestment } from '@/features/investments/co-investment/access';
import { CreateCoInvestmentRequestBody } from '@inversor-hub/types';
import { useGetMe } from '@/lib/api/me';
import useSnackbar from '@/hooks/useSnackbar';

const CreateCoInvestmentPage = () => {
  const navigate = useNavigate();
  const createCoInvestment = useCreateCoInvestment();
  const uploadMainImage = useUploadInvestmentMainImage();
  const { data: currentUser, isLoading: isLoadingUser } = useGetMe();
  const { show: showSnackbar } = useSnackbar();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasRedirectedRef = useRef(false);

  const canAccess = canCreateCoInvestment(currentUser);

  useEffect(() => {
    if (isLoadingUser || canAccess || hasRedirectedRef.current) {
      return;
    }

    hasRedirectedRef.current = true;
    showSnackbar({ title: 'No tienes permisos para crear co-inversiones', variant: 'error' });
    navigate('/inversiones', { replace: true });
  }, [isLoadingUser, canAccess, navigate, showSnackbar]);

  const handleSubmit = async (
    payload: CreateCoInvestmentRequestBody,
    mainImageFile?: File | null
  ) => {
    try {
      setError(null);
      setIsLoading(true);
      const result = await createCoInvestment.mutateAsync(payload);

      if (mainImageFile && result?.id) {
        await uploadMainImage.mutateAsync({
          investmentId: result.id,
          file: mainImageFile,
        });
      }

      showSnackbar({ title: 'Co-inversión creada', variant: 'success' });
      navigate('/inversiones');
    } catch (e) {
      setError('Error al crear la co-inversión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/inversiones');
  };

  if (isLoadingUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!canAccess) {
    return null;
  }

  return (
    <GoogleMapsProvider>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mb: 2 }}>
            Volver a inversiones
          </Button>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Nueva co-inversión
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registra una inversión compartida entre varios inversores
          </Typography>
        </Box>

        <CoInvestmentWizard
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          error={error}
          onErrorDismiss={() => setError(null)}
        />
      </Container>
    </GoogleMapsProvider>
  );
};

export default CreateCoInvestmentPage;
